import { useEffect, useState } from 'react';
import { useRouter } from 'next/router';

import { Box, Combobox, FormField, Heading, Input, Label, Modal, Paragraph } from '@zillow/constellation-10';

import { useAddressAutocomplete } from '../../../hooks/queries/address';
import useCloneListing from '../../../hooks/queries/useCloneListing';

import { getCreateListingRoute } from '../../../constants/urls';

import ModalFooter from '../ModalFooter';

import type { ChangeEvent } from 'react';
import type { ComboboxOptionType } from '@zillow/constellation-10';

type Props = {
  listingAlias: string;
  unit?: string;
  formattedAddressWithoutUnit: string;
  onClose: () => void;
};

const CloneThisListingConversionModal = ({ listingAlias, unit, formattedAddressWithoutUnit, onClose }: Props) => {
  const router = useRouter();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [newAddress, setNewAddress] = useState(formattedAddressWithoutUnit);
  const [newUnit, setNewUnit] = useState(unit ?? '');
  const [submitErrorMessage, setSubmitErrorMessage] = useState('');

  const [setPartialAddress, queryData] = useAddressAutocomplete();
  const { mutateAsync: createCloneListing } = useCloneListing();

  useEffect(() => {
    if (submitErrorMessage) {
      setSubmitErrorMessage('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newAddress, newUnit]);

  const handleSelectAddress = (option: ComboboxOptionType) => {
    const addressValue = typeof option === 'string' ? option : option.value;
    setNewAddress(addressValue);
  };

  const handleNewAddressChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setNewAddress(e.target.value);
    setPartialAddress(e.target.value);
  };

  const handleNewUnitChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.value.length > 20) return;
    setNewUnit(e.target.value);
  };

  const handleSubmit = () => {
    if (newUnit === (unit ?? '') && newAddress === formattedAddressWithoutUnit) {
      // eslint-disable-next-line constellation/word-list
      setSubmitErrorMessage('The new listing must have different unit number or address.');
      return;
    }

    setIsSubmitting(true);

    createCloneListing({ getParams: { listingAlias, fullAddress: newAddress, unit: newUnit || undefined } })
      .then(({ listingAlias: clonedListingAlias }) => {
        router.push(
          getCreateListingRoute({
            listingAlias: clonedListingAlias,
            step: 'REVIEW',
            listingCloneConversionSuccess: true,
          }),
        );
      })
      .catch((errorResponse: unknown) => {
        const message = (errorResponse as { error?: { message?: string } })?.error?.message ?? 'An error occurred';
        setSubmitErrorMessage(message);
        setIsSubmitting(false);
      });
  };

  return (
    <Modal
      open={true}
      onOpenChange={(open) => !open && !isSubmitting && onClose()}
      header={<Heading level={2}>Clone this listing</Heading>}
      useDismissProps={{ outsidePress: !isSubmitting }}
      size="md"
      body={
        <Box css={{ display: 'flex', flexDirection: 'column', gap: '400' }}>
          <Paragraph>
            If you need to list a similar property, you can create a copy of this listing. Your new listing will be
            pre-filled with the previous listing&apos;s details. To get started, change the address.
          </Paragraph>

          <Box css={{ display: 'flex', flexDirection: 'column', gap: '600', marginBottom: '800' }}>
            <FormField
              label={<Label>New listing address</Label>}
              required={true}
              control={
                <Combobox
                  appearance="input"
                  focusFirstOption={true}
                  options={
                    queryData?.data?.addresses?.map((address: { name: string }) => ({
                      ...address,
                      value: address.name,
                      label: address.name,
                    })) ?? []
                  }
                  onClear={() => handleNewAddressChange({ target: { value: '' } } as ChangeEvent<HTMLInputElement>)}
                  onOptionSelect={handleSelectAddress}
                  selectCallbackFrequency="always"
                  renderInput={() => <Combobox.Input value={newAddress} onChange={handleNewAddressChange} />}
                  renderEmptyState={() => newAddress.length <= 7 && null}
                  value=""
                />
              }
            />

            <FormField
              label={<Label>New unit number</Label>}
              control={<Input value={newUnit} onChange={handleNewUnitChange} />}
            />
          </Box>
        </Box>
      }
      footer={
        <ModalFooter
          hasError={!!submitErrorMessage}
          isSubmitting={isSubmitting}
          errorMessage={submitErrorMessage}
          submitText="Create"
          onSubmit={handleSubmit}
          onCancel={onClose}
        />
      }
    />
  );
};

export default CloneThisListingConversionModal;
